"use client";

import { useState } from "react";
import { Frame, Verdict, Note } from "./parts";

const L = {
    fr: {
        title: "Modifier par un nom, le voir changer par l'autre",
        append: "alias.append(\"d\")",
        appendCopy: "copie.append(\"z\")",
        reset: "Repartir de zéro",
        same: "même objet",
        other: "objet distinct",
        start: "Au départ, les trois noms affichent le même contenu. Rien ne permet de deviner lequel partage sa mémoire avec les autres.",
        afterAlias: "Un seul append, et deux noms ont changé. alias ne contenait pas une copie mais une seconde étiquette posée sur le même objet : les modifier revient au même.",
        afterCopy: "La copie a bougé seule. C'est la preuve qu'elle occupe bien une autre place en mémoire, et que le lien avec l'original a été rompu à sa création.",
        note: "Un test avec is répond immédiatement à la question : original is alias vaut True, original is copie vaut False. C'est le premier réflexe de diagnostic quand une donnée change sans que rien ne semble y toucher.",
    },
    en: {
        title: "Change it through one name, watch it change through the other",
        append: "alias.append(\"d\")",
        appendCopy: "copy.append(\"z\")",
        reset: "Start over",
        same: "same object",
        other: "separate object",
        start: "At the start all three names show the same content. Nothing hints at which one shares its memory with the others.",
        afterAlias: "One append, and two names changed. alias was never a copy but a second label stuck on the same object: changing either one is the same act.",
        afterCopy: "The copy moved on its own. That is the proof it occupies a different place in memory, and that the link to the original was cut when it was created.",
        note: "A test with is answers the question at once: original is alias gives True, original is copy gives False. It is the first diagnostic reflex when data changes while nothing seems to touch it.",
    },
};

const DEPART = ["a", "b", "c"];

export default function AliasVsCopy({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [partage, setPartage] = useState(DEPART);
    const [copie, setCopie] = useState(DEPART);
    const [dernier, setDernier] = useState("start");

    const nomCopie = lang === "fr" ? "copie" : "copy";

    const ligne = (nom, contenu, lie) => (
        <div className="flex items-center gap-3 py-2">
            <span className="font-geistMono text-sm w-20 shrink-0 dark:text-white">{nom}</span>
            <span className="font-geistMono text-sm flex-1 dark:text-lightBlue">
                [{contenu.map((x) => `'${x}'`).join(", ")}]
            </span>
            <span
                className={`text-[10px] uppercase tracking-widest shrink-0 ${
                    lie ? "text-primary" : "text-gray-400 dark:text-grayDark-500"
                }`}
            >
                {lie ? t.same : t.other}
            </span>
        </div>
    );

    return (
        <Frame icon="link-broken-linear" title={t.title}>
            <div className="divide-y divide-gray-100 dark:divide-white/10">
                {ligne("original", partage, true)}
                {ligne("alias", partage, true)}
                {ligne(nomCopie, copie, false)}
            </div>

            <div className="flex flex-wrap gap-2 mt-5">
                <button
                    onClick={() => {
                        setPartage((v) => [...v, "d"]);
                        setDernier("alias");
                    }}
                    className="rounded-lg bg-primary px-3 py-2 font-geistMono text-xs font-bold text-white transition-opacity duration-200 hover:opacity-90"
                >
                    {t.append}
                </button>
                <button
                    onClick={() => {
                        setCopie((v) => [...v, "z"]);
                        setDernier("copie");
                    }}
                    className="rounded-lg bg-gray-100 px-3 py-2 font-geistMono text-xs font-bold text-gray-700 transition-colors duration-200 hover:bg-gray-200 dark:bg-white/10 dark:text-grayDark-200 dark:hover:bg-white/20"
                >
                    {t.appendCopy}
                </button>
                <button
                    onClick={() => {
                        setPartage(DEPART);
                        setCopie(DEPART);
                        setDernier("start");
                    }}
                    className="rounded-lg px-3 py-2 text-xs text-gray-500 underline transition-colors duration-200 hover:text-gray-700 dark:text-grayDark-400 dark:hover:text-grayDark-200"
                >
                    {t.reset}
                </button>
            </div>

            <Verdict tone={dernier === "alias" ? "warn" : dernier === "copie" ? "good" : "neutral"}>
                {dernier === "alias" ? t.afterAlias : dernier === "copie" ? t.afterCopy : t.start}
            </Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
